import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';

const BOARD_TYPES = ['', 'CBSE', 'ICSE', 'State Board', 'IB', 'Cambridge', 'Other'];
const TABS = ['School Info', 'Admin Account', 'Branches'];

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const emptyBranch = () => ({ branchName: '', address: '', city: '', state: '', phone: '', email: '', principalName: '' });

const emptyForm = () => ({
  schoolName: '', schoolCode: '', address: '', city: '', state: '', pincode: '',
  phone: '', email: '', website: '', establishedYear: '', boardType: '',
  adminName: '', adminEmail: '', adminPhone: '', adminPassword: '',
  branches: []
});

const IconEdit = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);
const IconTrash = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const IconEye = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const SchoolManagement = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [viewingSchool, setViewingSchool] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [newBranch, setNewBranch] = useState(emptyBranch());
  const [addingBranch, setAddingBranch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const fetchSchools = async () => {
    try {
      const res = await axios.get(`${API_BASE}/schools`, { headers: headers() });
      setSchools(res.data);
    } catch (err) {
      flash('error', err.response?.data?.error || 'Failed to load schools');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSchools(); }, []);

  const validate = () => {
    const e = {};
    if (!form.schoolName.trim()) e.schoolName = 'School name is required';
    if (!form.adminName.trim()) e.adminName = 'Admin name is required';
    if (!form.adminEmail.trim()) e.adminEmail = 'Admin email is required';
    if (!editingSchool && !form.adminPassword.trim()) e.adminPassword = 'Password is required';
    if (form.adminEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.adminEmail)) e.adminEmail = 'Invalid email format';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email format';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openAdd = () => {
    setForm(emptyForm());
    setErrors({});
    setActiveTab(0);
    setEditingSchool(null);
    setNewBranch(emptyBranch());
    setAddingBranch(false);
    setShowModal(true);
  };

  const openEdit = (school) => {
    setEditingSchool(school);
    setForm({
      schoolName: school.schoolName || '',
      schoolCode: school.schoolCode || '',
      address: school.address || '',
      city: school.city || '',
      state: school.state || '',
      pincode: school.pincode || '',
      phone: school.phone || '',
      email: school.email || '',
      website: school.website || '',
      establishedYear: school.establishedYear || '',
      boardType: school.boardType || '',
      adminName: school.adminName || '',
      adminEmail: school.adminEmail || '',
      adminPhone: school.adminPhone || '',
      adminPassword: '',
      branches: school.branches || []
    });
    setErrors({});
    setActiveTab(0);
    setNewBranch(emptyBranch());
    setAddingBranch(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { if (errors.schoolName || errors.adminEmail) setActiveTab(0); else if (errors.adminPassword || errors.adminName) setActiveTab(1); return; }
    setSaving(true);
    try {
      if (editingSchool) {
        await axios.put(`${API_BASE}/schools/${editingSchool._id}`, form, { headers: headers() });
        flash('success', 'School updated successfully');
      } else {
        await axios.post(`${API_BASE}/schools`, form, { headers: headers() });
        flash('success', 'School created successfully with admin account');
      }
      setShowModal(false);
      fetchSchools();
    } catch (err) {
      flash('error', err.response?.data?.error || 'Failed to save school');
    } finally { setSaving(false); }
  };

  const handleDelete = async (school) => {
    if (!window.confirm(`Delete "${school.schoolName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/schools/${school._id}`, { headers: headers() });
      flash('success', 'School deleted');
      fetchSchools();
    } catch (err) { flash('error', err.response?.data?.error || 'Failed to delete'); }
  };

  const handleToggle = async (school) => {
    try {
      const res = await axios.patch(`${API_BASE}/schools/${school._id}/toggle`, {}, { headers: headers() });
      setSchools(prev => prev.map(s => s._id === school._id ? { ...s, isActive: res.data.isActive } : s));
    } catch (err) { flash('error', 'Failed to toggle status'); }
  };

  const addBranchToForm = () => {
    if (!newBranch.branchName.trim()) return;
    setForm(f => ({ ...f, branches: [...f.branches, { ...newBranch }] }));
    setNewBranch(emptyBranch());
    setAddingBranch(false);
  };

  const removeBranchFromForm = (idx) => {
    setForm(f => ({ ...f, branches: f.branches.filter((_, i) => i !== idx) }));
  };

  const filtered = schools.filter(s =>
    s.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.adminEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.schoolCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: schools.length,
    active: schools.filter(s => s.isActive).length,
    branches: schools.reduce((sum, s) => sum + (s.branches?.length || 0), 0)
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">School Management</h1>
              <p className="text-xs text-gray-400 mt-0.5">Manage schools and their admin accounts</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="flex-1 overflow-auto p-6 space-y-5">
          {msg.text && (
            <div className={`px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {msg.text}
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Schools', value: stats.total, icon: '🏫', color: 'bg-indigo-50 text-indigo-700' },
              { label: 'Active', value: stats.active, icon: '✅', color: 'bg-green-50 text-green-700' },
              { label: 'Total Branches', value: stats.branches, icon: '🏢', color: 'bg-blue-50 text-blue-700' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-lg shadow p-5 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${s.color}`}>{s.icon}</div>
                <div>
                  <div className="text-2xl font-bold text-gray-800">{s.value}</div>
                  <div className="text-xs text-gray-500">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
            <input
              type="text"
              placeholder="Search by school name, city, admin email, code..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button onClick={openAdd}
              className="bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2 whitespace-nowrap font-medium">
              <span className="text-lg leading-none">+</span> Add New School
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">School</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Board</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Admin</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Branches</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-400">Loading...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-14 text-center">
                        <div className="text-4xl mb-2">🏫</div>
                        <p className="text-gray-500">{searchTerm ? 'No schools match your search.' : 'No schools found. Click "Add New School" to start.'}</p>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((school, idx) => (
                      <tr key={school._id} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-4 text-sm text-gray-500">{idx + 1}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                              {school.schoolName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-800 text-sm">{school.schoolName}</div>
                              {school.city && <div className="text-xs text-gray-400">{school.city}{school.state ? `, ${school.state}` : ''}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm font-mono text-gray-600">{school.schoolCode || <span className="text-gray-300">—</span>}</td>
                        <td className="px-5 py-4">
                          {school.boardType ? <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">{school.boardType}</span> : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-sm font-medium text-gray-700">{school.adminName}</div>
                          <div className="text-xs text-gray-400">{school.adminEmail}</div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                            {school.branches?.length || 0} branch{school.branches?.length !== 1 ? 'es' : ''}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <button onClick={() => handleToggle(school)}
                            className={`px-2 py-1 rounded-full text-xs font-semibold transition ${school.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>
                            {school.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setViewingSchool(school)} title="View" className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"><IconEye /></button>
                            <button onClick={() => openEdit(school)} title="Edit" className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition"><IconEdit /></button>
                            <button onClick={() => handleDelete(school)} title="Delete" className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"><IconTrash /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {filtered.length > 0 && (
              <div className="px-5 py-3 border-t text-sm text-gray-500">Showing {filtered.length} of {schools.length} schools</div>
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      {viewingSchool && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">{viewingSchool.schoolName}</h2>
                <p className="text-indigo-200 text-xs">{viewingSchool.schoolCode || 'No code'} · {viewingSchool.boardType || 'No board set'}</p>
              </div>
              <button onClick={() => setViewingSchool(null)} className="text-white text-2xl hover:text-indigo-200">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['City / State', viewingSchool.city ? `${viewingSchool.city}${viewingSchool.state ? ', ' + viewingSchool.state : ''}` : '—'],
                  ['Phone', viewingSchool.phone || '—'],
                  ['Email', viewingSchool.email || '—'],
                  ['Website', viewingSchool.website || '—'],
                  ['Established', viewingSchool.establishedYear || '—'],
                ].map(([label, val]) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className="font-semibold text-gray-800 text-sm break-all">{val}</p>
                  </div>
                ))}
              </div>
              <div className="bg-indigo-50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Admin Account</p>
                <p className="font-semibold text-gray-800 text-sm">{viewingSchool.adminName}</p>
                <p className="text-xs text-gray-500">{viewingSchool.adminEmail} · {viewingSchool.adminPhone || 'No phone'}</p>
              </div>
              {viewingSchool.branches?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Branches ({viewingSchool.branches.length})</p>
                  <div className="space-y-2">
                    {viewingSchool.branches.map((b, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-3">
                        <p className="font-semibold text-gray-800 text-sm">{b.branchName}</p>
                        {b.city && <p className="text-xs text-gray-400">{b.city}{b.state ? `, ${b.state}` : ''}</p>}
                        {b.principalName && <p className="text-xs text-gray-500">Principal: {b.principalName}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 pb-4 flex justify-end gap-2">
              <button onClick={() => { setViewingSchool(null); openEdit(viewingSchool); }} className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm">Edit</button>
              <button onClick={() => setViewingSchool(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">{editingSchool ? 'Edit School' : 'Add New School'}</h3>
              <button onClick={() => setShowModal(false)} className="text-white text-2xl hover:text-indigo-200">✕</button>
            </div>

            {/* Tabs */}
            <div className="flex border-b bg-gray-50">
              {TABS.map((tab, i) => (
                <button key={tab} onClick={() => setActiveTab(i)}
                  className={`flex-1 py-3 text-sm font-medium transition border-b-2 ${activeTab === i ? 'border-indigo-600 text-indigo-700 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {tab} {i === 2 && form.branches.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">{form.branches.length}</span>}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Tab 0: School Info */}
              {activeTab === 0 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">School Name *</label>
                      <input type="text" required value={form.schoolName} onChange={e => setForm(f => ({ ...f, schoolName: e.target.value }))}
                        className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.schoolName ? 'border-red-400' : ''}`} />
                      {errors.schoolName && <p className="text-xs text-red-500 mt-1">{errors.schoolName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">School Code</label>
                      <input type="text" value={form.schoolCode} onChange={e => setForm(f => ({ ...f, schoolCode: e.target.value }))}
                        placeholder="e.g. SKL001" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Board Type</label>
                      <select value={form.boardType} onChange={e => setForm(f => ({ ...f, boardType: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                        {BOARD_TYPES.map(b => <option key={b} value={b}>{b || '— Select Board —'}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input type="tel" inputMode="numeric" maxLength={10} value={form.phone}
                        onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                        placeholder="10-digit number" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.email ? 'border-red-400' : ''}`} />
                      {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input type="text" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                      <input type="text" value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Established Year</label>
                      <input type="text" value={form.establishedYear} onChange={e => setForm(f => ({ ...f, establishedYear: e.target.value }))}
                        placeholder="e.g. 1998" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <input type="url" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                        placeholder="https://..." className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <textarea value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} rows="2"
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 1: Admin Account */}
              {activeTab === 1 && (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                    {editingSchool ? 'Update admin account details. Leave password blank to keep unchanged.' : 'Create the admin login account for this school. A welcome email will be sent to the admin email address.'}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Full Name *</label>
                    <input type="text" value={form.adminName} onChange={e => setForm(f => ({ ...f, adminName: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.adminName ? 'border-red-400' : ''}`} />
                    {errors.adminName && <p className="text-xs text-red-500 mt-1">{errors.adminName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email *</label>
                    <input type="email" value={form.adminEmail} onChange={e => setForm(f => ({ ...f, adminEmail: e.target.value }))}
                      readOnly={!!editingSchool}
                      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.adminEmail ? 'border-red-400' : ''} ${editingSchool ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                    {errors.adminEmail && <p className="text-xs text-red-500 mt-1">{errors.adminEmail}</p>}
                    {editingSchool && <p className="text-xs text-gray-400 mt-1">Email cannot be changed after creation</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Phone</label>
                    <input type="tel" inputMode="numeric" maxLength={10} value={form.adminPhone}
                      onChange={e => setForm(f => ({ ...f, adminPhone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      placeholder="10-digit number" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{editingSchool ? 'New Password' : 'Password *'}</label>
                    <div className="relative">
                      <input type={showPass ? 'text' : 'password'} value={form.adminPassword} onChange={e => setForm(f => ({ ...f, adminPassword: e.target.value }))}
                        placeholder={editingSchool ? 'Leave blank to keep current' : 'Min 6 characters'}
                        className={`w-full border rounded-lg px-3 py-2 pr-16 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.adminPassword ? 'border-red-400' : ''}`} />
                      <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600">
                        {showPass ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {errors.adminPassword && <p className="text-xs text-red-500 mt-1">{errors.adminPassword}</p>}
                  </div>
                </div>
              )}

              {/* Tab 2: Branches */}
              {activeTab === 2 && (
                <div className="space-y-4">
                  {form.branches.length === 0 && !addingBranch && (
                    <div className="text-center py-6 text-gray-400 text-sm">No branches added yet</div>
                  )}
                  {form.branches.map((b, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-800">{b.branchName}</p>
                          <p className="text-xs text-gray-500">{b.city}{b.state ? `, ${b.state}` : ''} {b.phone ? `· ${b.phone}` : ''}</p>
                          {b.principalName && <p className="text-xs text-gray-400">Principal: {b.principalName}</p>}
                        </div>
                        <button type="button" onClick={() => removeBranchFromForm(i)} className="p-1 text-red-400 hover:text-red-600 transition"><IconTrash /></button>
                      </div>
                    </div>
                  ))}
                  {addingBranch ? (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
                      <p className="text-sm font-semibold text-indigo-800">New Branch</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <input type="text" value={newBranch.branchName} onChange={e => setNewBranch(b => ({ ...b, branchName: e.target.value }))}
                            placeholder="Branch Name *" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        {[['city', 'City'], ['state', 'State'], ['phone', 'Phone'], ['email', 'Email'], ['principalName', 'Principal Name']].map(([k, lbl]) => (
                          <input key={k} type="text" value={newBranch[k]} onChange={e => setNewBranch(b => ({ ...b, [k]: e.target.value }))}
                            placeholder={lbl} className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        ))}
                      </div>
                      <div className="col-span-2">
                        <input type="text" value={newBranch.address} onChange={e => setNewBranch(b => ({ ...b, address: e.target.value }))}
                          placeholder="Address" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button type="button" onClick={addBranchToForm} className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">Add Branch</button>
                        <button type="button" onClick={() => { setAddingBranch(false); setNewBranch(emptyBranch()); }} className="px-4 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setAddingBranch(true)}
                      className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50 transition">
                      + Add Branch
                    </button>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center pt-5 border-t mt-5">
                <div className="flex gap-2">
                  {activeTab > 0 && <button type="button" onClick={() => setActiveTab(t => t - 1)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100 text-sm">← Back</button>}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100 text-sm">Cancel</button>
                  {activeTab < TABS.length - 1
                    ? <button type="button" onClick={() => setActiveTab(t => t + 1)} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium">Next →</button>
                    : <button type="submit" disabled={saving} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50">{saving ? 'Saving...' : editingSchool ? 'Update School' : 'Create School'}</button>
                  }
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolManagement;
